import { useCallback, useRef } from 'react';
import {
  useMutation as useQueryMutation,
  type DefaultError,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

/** What makes two calls "the same": the payload, with files by name, size and date. */
export function mutationKey(vars: unknown): string {
  try {
    return JSON.stringify(vars ?? null, (_k, v: unknown) =>
      typeof Blob !== 'undefined' && v instanceof Blob
        ? { blob: v.size, type: v.type, name: (v as File).name, at: (v as File).lastModified }
        : v,
    );
  } catch {
    return String(Math.random()); // not serialisable: never joined
  }
}

/**
 * `useMutation` with a double-submit guard, for every write in the data layer: a second call
 * with the same payload while the first is in flight (a double tap, Enter and a click, a
 * re-render that submits again) joins the first call's promise instead of sending again. A
 * different payload goes as usual.
 */
export function useMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const m = useQueryMutation(options);
  const inflight = useRef(new Map<string, Promise<TData>>());
  const { mutateAsync: send } = m;

  const mutateAsync = useCallback(
    (vars: TVariables, opts?: MutateOptions<TData, TError, TVariables, TContext>) => {
      const key = mutationKey(vars);
      const running = inflight.current.get(key);
      if (running) return running;
      const p = send(vars, opts).finally(() => inflight.current.delete(key));
      inflight.current.set(key, p);
      return p;
    },
    [send],
  );
  const mutate = useCallback(
    (vars: TVariables, opts?: MutateOptions<TData, TError, TVariables, TContext>) => {
      mutateAsync(vars, opts).catch(() => {
        /* reported through opts.onError and the mutation's state, as with useMutation */
      });
    },
    [mutateAsync],
  );
  return { ...m, mutate, mutateAsync } as UseMutationResult<TData, TError, TVariables, TContext>;
}
